import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { colorPalette } from "../styles/theme";
import { useWsDataset, buildTriageRows as buildWsTriageRows } from "./wsData";
import { PHQ_BANDS, GAD_BANDS, getBandLabel } from "./screeningBands";

/**
 * 우선 확인 필요 대상자 (Action Required Triage Table)
 * - 선택 기간 내 위험 신호가 잡힌 참가자만 우선순위별로 자동 추출해서 보여줍니다.
 * - 기본 정렬: 긴급 위기 → 에피소드 진행 중 → 디스트레스 고위험(만성) → 에피소드 후 잠잠 전환
 *   (같은 등급 안에서는 최근 디스트레스가 높은 순)
 */

const INITIAL_VISIBLE_ROWS = 10;
const SPARKLINE = { width: 80, height: 24, padding: 3 };

// 위험 등급 — priority가 낮을수록 먼저 확인해야 하는 대상
const RISK_LEVELS = {
  crisis: {
    priority: 1,
    label: "🚨 긴급위기",
    reason: "대화/기록 내 안전 관련 주의 신호 포착",
    sx: { backgroundColor: "#B91C1C", color: "#FFFFFF" },
  },
  episode: {
    priority: 2,
    label: "⚡ 에피소드 진행중",
    reason: "최근 스트레스 지수 급격한 상승 연속 감지",
    sx: { backgroundColor: "#FFEDD5", color: "#C2410C", border: "1px solid #FDBA74" },
  },
  chronic: {
    priority: 3,
    label: "🔴 만성 고위험",
    reason: "전반적 디스트레스 높은 수준 지속",
    sx: { backgroundColor: "#FEE2E2", color: "#B91C1C" },
  },
  dropout: {
    priority: 4,
    label: "⚠️ 이탈주의(잠잠)",
    reason: "에피소드 발생 후 7일 이상 기록 중단",
    sx: { backgroundColor: "#FEF3C7", color: "#92400E" },
  },
};
const RISK_LEVEL_ORDER = Object.keys(RISK_LEVELS).sort(
  (a, b) => RISK_LEVELS[a].priority - RISK_LEVELS[b].priority,
);

const ACTIVITY_BADGE = {
  active: { label: "활발", sx: { backgroundColor: "#DCFCE7", color: "#15803D" } },
  inactive: { label: "잠잠", sx: { backgroundColor: colorPalette.secondary, color: "#6B7280" } },
};

// 스파크라인 툴팁용 디스트레스 단계 (종합현황 "기관 평균 마음 상태"와 같은 구간)
const getDistressStage = (value) => {
  if (value <= 0.16) return "안정";
  if (value <= 0.41) return "주의";
  if (value <= 0.6) return "경고";
  return "위험";
};

// ─── 데이터 집계 ───────────────────────────────────────────────────
/**
 * CSV 기준 우선 확인 대상자 — 등급 판정은 wsData.buildTriageRows 참고
 * (같은 등급 안에서는 최근 디스트레스가 높은 순)
 */
const buildTriageRows = (dataset, scopeKey, range) =>
  buildWsTriageRows(dataset, scopeKey, range.start, range.end).sort(
    (a, b) =>
      RISK_LEVELS[a.level].priority - RISK_LEVELS[b.level].priority ||
      (b.latestDistress ?? -1) - (a.latestDistress ?? -1),
  );

// ─── UI 구성 요소 ──────────────────────────────────────────────────
const Badge = ({ label, sx }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      px: 1,
      py: 0.4,
      borderRadius: 1,
      fontSize: 12,
      fontWeight: 700,
      lineHeight: 1.3,
      whiteSpace: "nowrap",
      ...sx,
    }}
  >
    {label}
  </Box>
);

/** 최근 7일 디스트레스 미니 꺾은선 (80×24) — 에피소드 발생일은 빨간 점 */
const Sparkline = ({ data }) => {
  const { width, height, padding } = SPARKLINE;
  const hasValue = data.some((p) => p.value !== null);

  if (!hasValue) {
    return (
      <Box sx={{ width, height, display: "flex", alignItems: "center" }}>
        <Typography sx={{ fontSize: 11, color: colorPalette.textSecondary, whiteSpace: "nowrap" }}>
          기록 없음
        </Typography>
      </Box>
    );
  }

  const step = (width - padding * 2) / (data.length - 1);
  const x = (i) => padding + step * i;
  const y = (value) => padding + (1 - value) * (height - padding * 2);

  // 값이 없는 날은 선을 끊음
  let path = "";
  let drawing = false;
  data.forEach((p, i) => {
    if (p.value === null) {
      drawing = false;
      return;
    }
    path += `${drawing ? "L" : "M"} ${x(i).toFixed(1)} ${y(p.value).toFixed(1)} `;
    drawing = true;
  });

  const tooltip = (
    <Box sx={{ lineHeight: 1.6 }}>
      {data.map((p) => (
        <Box key={p.date}>
          {p.date.slice(5)} · {p.value === null ? "기록 없음" : getDistressStage(p.value)}
          {p.episode ? " (에피소드)" : ""}
        </Box>
      ))}
    </Box>
  );

  return (
    <Tooltip title={tooltip} placement="left" arrow>
      <Box component="svg" width={width} height={height} sx={{ display: "block", cursor: "default" }}>
        <title>최근 7일 디스트레스 추이</title>
        <path d={path.trim()} fill="none" stroke={colorPalette.textSecondary} strokeWidth="1.5" strokeLinejoin="round" />
        {data.map((p, i) =>
          p.value !== null && p.episode ? (
            <circle key={p.date} cx={x(i)} cy={y(p.value)} r="2.5" fill="#EF4444" stroke="#FFFFFF" strokeWidth="1" />
          ) : null,
        )}
      </Box>
    </Tooltip>
  );
};

/** 최근 선별검사 결과 — 미실시는 회색 텍스트 */
const ScreeningResult = ({ label, score, bands }) => (
  <Typography sx={{ fontSize: 12, whiteSpace: "nowrap", color: colorPalette.textPrimary }}>
    <Box component="span" sx={{ color: colorPalette.textSecondary, mr: 0.5 }}>
      {label}:
    </Box>
    {score === null ? (
      <Box component="span" sx={{ color: "#9CA3AF" }}>
        미실시
      </Box>
    ) : (
      `${getBandLabel(bands, score)}(${score}점)`
    )}
  </Typography>
);

const COLUMNS = [
  { key: "level", label: "위험 등급", width: 150 },
  { key: "participant", label: "참가자 정보", width: 150 },
  { key: "activity", label: "활동 상태", width: 90 },
  { key: "reason", label: "감지 사유 요약" },
  { key: "trend", label: "최근 7일 추이", width: 110 },
  { key: "screening", label: "최근 선별검사", width: 200 },
  { key: "action", label: "상세 및 조치", width: 120, align: "center" },
];

const genderLabel = (gender) => (gender === "M" ? "남" : gender === "F" ? "여" : "-");

/**
 * @param {string} scopeKey - 조회 범위 키
 * @param {object} range - 적용된 조회 기간 { start, end }
 * @param {object} summary - 종합현황 요약 (KPI 카드와 인원 수를 맞추기 위해 사용)
 * @param {Function} onOpenDetail - [상세 보기] 클릭 시 (row) => void
 */
const TriageTable = ({ scopeKey, range, summary, onOpenDetail }) => {
  const [expanded, setExpanded] = useState(false);
  const dataset = useWsDataset();
  const rows = useMemo(() => buildTriageRows(dataset, scopeKey, range), [dataset, scopeKey, range]);
  const visibleRows = expanded ? rows : rows.slice(0, INITIAL_VISIBLE_ROWS);

  const levelCounts = RISK_LEVEL_ORDER.map((level) => ({
    level,
    count: rows.filter((row) => row.level === level).length,
  })).filter(({ count }) => count > 0);

  return (
    <Paper variant="outlined" sx={{ mt: 3, backgroundColor: colorPalette.white, overflow: "hidden" }}>
      {/* 제목 + 등급별 인원 */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          px: 2.5,
          py: 2,
          borderBottom: `1px solid ${colorPalette.border}`,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: colorPalette.textPrimary }}>
              우선 확인 필요 대상자
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: colorPalette.primary }}>{rows.length}명</Typography>
          </Box>
          <Typography sx={{ mt: 0.5, fontSize: 12, color: colorPalette.textSecondary }}>
            선택 기간 내 위험 신호가 감지된 참가자를 우선순위 순으로 보여 줍니다.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {levelCounts.map(({ level, count }) => (
            <Badge key={level} label={`${RISK_LEVELS[level].label} ${count}`} sx={RISK_LEVELS[level].sx} />
          ))}
        </Box>
      </Box>

      {rows.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography sx={{ fontSize: 13, color: colorPalette.textSecondary }}>
            선택 기간 내 우선 확인이 필요한 참가자가 없습니다.
          </Typography>
        </Box>
      ) : (
        <>
          {/* 좁은 화면에서는 표만 가로 스크롤 */}
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align ?? "left"}
                      sx={{
                        width: column.width,
                        py: 1.25,
                        fontSize: 12,
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        color: colorPalette.textSecondary,
                        backgroundColor: colorPalette.background,
                        borderBottom: `1px solid ${colorPalette.border}`,
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row) => {
                  const level = RISK_LEVELS[row.level];
                  const activity = ACTIVITY_BADGE[row.activity];
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{
                        // 긴급 위기 행은 배경을 옅게 칠해 한 번 더 강조
                        backgroundColor: row.level === "crisis" ? "#FEF2F2" : "transparent",
                        "& td": { py: 1.25, borderBottom: `1px solid ${colorPalette.border}` },
                        "&:last-of-type td": { borderBottom: "none" },
                      }}
                    >
                      <TableCell>
                        <Badge label={level.label} sx={level.sx} />
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: colorPalette.textPrimary, whiteSpace: "nowrap" }}>
                          {row.name}{" "}
                          <Box component="span" sx={{ fontWeight: 400, color: colorPalette.textSecondary }}>
                            ({row.age ?? "-"}세 · {genderLabel(row.gender)})
                          </Box>
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Badge label={activity.label} sx={activity.sx} />
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 13, color: colorPalette.textPrimary }}>{level.reason}</Typography>
                      </TableCell>
                      <TableCell>
                        <Sparkline data={row.trend} />
                      </TableCell>
                      <TableCell>
                        <ScreeningResult label="PHQ-9" score={row.phq} bands={PHQ_BANDS} />
                        <ScreeningResult label="GAD-7" score={row.gad} bands={GAD_BANDS} />
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<ArrowForwardIcon sx={{ fontSize: "14px !important" }} />}
                          onClick={() => onOpenDetail?.(row)}
                          sx={{ height: 28, px: 1.25, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}
                        >
                          상세 보기
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {rows.length > INITIAL_VISIBLE_ROWS && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1, borderTop: `1px solid ${colorPalette.border}` }}>
              <Button size="small" onClick={() => setExpanded((prev) => !prev)} sx={{ fontSize: 12 }}>
                {expanded ? "접기" : `전체 ${rows.length}명 보기`}
              </Button>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default TriageTable;
